import { Fragment, useState } from 'react'
import type { BookSave, CallbackNumber, IncidentDetail, ThreadEvent } from '../lib/api'
import { api, ApiError } from '../lib/api'

/** WHERE ANOTHER NUMBER GOES IN THE PHONE BOOK (25 Oct 2026). The customer:
 *  under the caller "just with the label wife, daughter, son", or "if it's
 *  someone else completely, it should ask that person's name" and make a
 *  record of its own -- or nowhere. Only the phone book grows: the call keeps
 *  every number as given whichever is chosen. */
export function BookChoice({ save, name, label, caller, onChange, allowNo = true }: {
  save: BookSave
  name: string
  /** The number's note, which becomes its label under the caller. */
  label: string
  caller: string
  onChange: (save: BookSave, name: string) => void
  /** Off for a number already on a call: "nowhere" is what it is now. */
  allowNo?: boolean
}) {
  const who = caller.trim() || 'the caller'
  const note = label.trim()
  const why = save === 'caller'
    ? `${who === 'the caller' ? "The caller's" : `${who}'s`} record gets this number${note ? `, labelled ${note}` : ''}.`
    : save === 'someone'
      ? (name.trim() ? `A new record: ${name.trim()}, with this number.` : 'Type their name — it becomes a record of its own.')
      : 'Kept on this call only.'
  return (
    <div className="bookpick">
      <span>Save to the phone book:</span>
      <select value={save} aria-label="Save to the phone book"
              onChange={(e) => onChange(e.target.value as BookSave, name)}>
        <option value="caller">Under {who}{note ? `, as “${note}”` : ''}</option>
        <option value="someone">As someone else…</option>
        {allowNo && <option value="no">Don't save</option>}
      </select>
      {save === 'someone' && (
        <input value={name} maxLength={200} placeholder="Their name" aria-label="Their name"
               onChange={(e) => onChange(save, e.target.value)} />
      )}
      <span className="bookwhy">{why}</span>
    </div>
  )
}

/** "Save to the phone book…" on a number already on a call, open or closed
 *  -- the administrator going through old calls (25 Oct 2026). */
function FileNumber({ n, caller, onFile }: {
  n: CallbackNumber
  caller: string
  onFile: (n: CallbackNumber, save: 'caller' | 'someone', name: string | null) => Promise<void>
}) {
  const [open, setOpen] = useState(false)
  const [save, setSave] = useState<BookSave>('caller')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  if (!open) {
    return (
      <button type="button" className="ghost tiny bookopen" onClick={() => setOpen(true)}>
        Save to the phone book…
      </button>
    )
  }
  const ready = save === 'caller' || (save === 'someone' && !!name.trim())
  return (
    <div className="bookfile">
      <BookChoice save={save} name={name} label={n.label ?? ''} caller={caller} allowNo={false}
                  onChange={(s, nm) => { setSave(s); setName(nm) }} />
      {error && <div className="bookerr">{error}</div>}
      <div className="bookbtns">
        <button type="button" className="btn sm" disabled={!ready || busy}
                onClick={async () => {
                  setBusy(true)
                  setError(null)
                  try {
                    await onFile(n, save === 'someone' ? 'someone' : 'caller',
                                 save === 'someone' ? name.trim() : null)
                    setOpen(false)
                  } catch (e) {
                    setError(e instanceof ApiError ? e.message : 'Could not save it to the phone book.')
                  } finally {
                    setBusy(false)
                  }
                }}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="ghost tiny" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  )
}

/** The pieces of a call that the WORKING call and a PAST call both draw.
 *
 *  ONE COPY, because the Past calls screen is where an administrator checks
 *  what the dispatcher saw -- and two renderings of "who was sent what" would
 *  one day disagree about a failed text, which is the thing being checked. */

export type Sends = Awaited<ReturnType<typeof api.incidentBroadcasts>>

/** What each kind of message is called on the call's list of sends. */
export const SEND_KIND: Record<string, string> = {
  initial: 'first', rebroadcast: 'again', relocation: 'moved', combined: 'with other calls', combined_first: 'first, with other calls',
  collection: 'collect', details: 'details', site: 'codes', callback: 'callback',
  removed: 'taken off', note: 'note', call_closed: 'closed',
}
/** The ones that go to the members going rather than to everybody. */
export const HANDOVER = new Set(['details', 'site', 'callback', 'note'])

export function hhmm(v: string | null | undefined): string | null {
  return v
    ? new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
    : null
}

/** EVERY NUMBER TO REACH THE CALLER ON, as `<dt>/<dd>` rows for a `.kv` list:
 *  the one he called from, then the others with their notes (055). The working
 *  call passes `onRemove`; Past calls does not, and draws them read-only.
 *
 *  AS TYPED, NEVER THE READING. "514 555 1234 ext 23" is read by libphonenumber
 *  as +15145551234 -- the extension is not part of a number -- so drawing the
 *  reading put a switchboard on the screen the dispatcher dials from, while
 *  the text the member was sent kept the extension. What was typed is what
 *  both of them see. */
export function CallbackRows({
  callerPhone, numbers, onRemove, busy, callerName, onFile,
}: {
  callerPhone: string | null
  numbers: CallbackNumber[]
  onRemove?: (n: CallbackNumber) => void
  busy?: boolean
  /** For "Under Sample Caller" in the phone-book choice. */
  callerName?: string | null
  /** Saves one into the phone book (25 Oct 2026); absent for a role without
   *  the phone book's Add tick, which then only sees where each one is. */
  onFile?: (n: CallbackNumber, save: 'caller' | 'someone', name: string | null) => Promise<void>
}) {
  return (
    <>
      <dt>Called from</dt>
      <dd className="mono">{callerPhone || '—'}</dd>
      {numbers.map((n) => (
        // A FRAGMENT, not a wrapper: a <dl> holds loose dt/dd pairs or div
        // groups, never both, and the rows around this one are loose.
        <Fragment key={n.id}>
          <dt>{n.label ?? 'Other number'}</dt>
          <dd>
            <span className="mono">{n.phone_raw}</span>
            {/* WHERE IT IS IN THE PHONE BOOK, read each time (25 Oct 2026). */}
            {n.book_is_callers
              ? <span className="pill go bookpill">in the phone book under the caller</span>
              : n.book_name
                ? <span className="pill bookpill bookother">in the phone book as {n.book_name}</span>
                : onFile && n.phone_e164 && <FileNumber n={n} caller={callerName ?? ''} onFile={onFile} />}
            {onRemove && (
              <button type="button" className="ghost tiny" disabled={busy}
                      title="Remove this number from the call"
                      onClick={() => onRemove(n)}>
                ✕
              </button>
            )}
          </dd>
        </Fragment>
      ))}
    </>
  )
}

/** Every message the call sent, and what became of each delivery. */
/** EVERY CALLER, IN THE ORDER THEY RANG (063): the call's own caller first,
 *  then each report with what he added and his differing answers. Drawn on
 *  the working call and on Past calls alike. */
export function CallerRows({ call }: { call: IncidentDetail }) {
  if (!call.reports || call.reports.length === 0) return null
  return (
    <div className="box" style={{ marginTop: 15 }}>
      <h4>
        Callers
        <span className="pill wait">{call.reports.length + 1}</span>
        <span className="lbl" style={{ marginLeft: 'auto' }}>in the order they rang</span>
      </h4>
      <div className="bd">
        <div className="caller">
          <span className="n mono">1</span>
          <div className="who">
            <b>{call.caller_name ?? 'Unknown caller'}</b>
            {call.caller_phone && <span className="mono dim"> {call.caller_phone}</span>}
            <span className="pill" style={{ marginLeft: 6 }}>first caller</span>
            {call.relationship_label && (
              <div className="said">Calling for {call.relationship_label.toLowerCase()}.</div>
            )}
          </div>
          <span className="when mono">{call.called_at_unknown ? 'time not known' : hhmm(call.called_at)}</span>
        </div>
        {call.reports.map((r) => (
          <div className="caller" key={r.id}>
            <span className="n mono">{r.sequence + 1}</span>
            <div className="who">
              {r.anonymous && <span className="anonpill">Anonymous · not to be given out</span>}
              <b>{r.caller_name ?? 'No name given'}</b>
              {r.caller_phone && <span className="mono dim"> {r.caller_phone}</span>}
              {r.relationship_label && <span className="dim"> · for {r.relationship_label.toLowerCase()}</span>}
              {r.said && <div className="said">{r.said}</div>}
              {r.answers && Object.keys(r.answers).length > 0 && (
                <div className="said">
                  {Object.entries(r.answers).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                </div>
              )}
            </div>
            <span className="when mono">{hhmm(r.reported_at)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export function SendRows({ sends }: { sends: Sends }) {
  return (
    <>
      {sends.map((b) => (
        <div className="resp" key={b.id}>
          <span className="cs">{SEND_KIND[b.kind] ?? b.kind}</span>
          {/* SEVERAL CALLS IN ITS LINES: run together, each call's words read
              as the end of the call before when they go above it (`line_place`). */}
          <div className={b.kind === 'combined' ? 'nm lines' : 'nm'}>
            {b.body}
            <small>
              {hhmm(b.sent_at)}
              {' · '}{b.recipient_count} member(s)
              {b.kind === 'removed'
                ? ' taken off the call'
                : b.kind === 'call_closed'
                  ? ' who went'
                : HANDOVER.has(b.kind)
                  ? ' going'
                  : ` · ${b.audience ? b.audience.join(', ') : 'everyone on duty'}`}
              {b.first_error && ` · ${b.first_error}`}
            </small>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 5 }}>
            {/* Delivered first, because it is the strongest claim: the text
                server says it reached the handset. `sent` below it means only
                that the carrier took it.

                Every status the server counts is drawn. These three used to be
                the whole list, and `delivered` -- legal since migration 007,
                set by nobody -- would have matched none of them: a broadcast
                where everybody was reached rendered no pill at all, which reads
                as "nothing has happened". */}
            {b.delivered > 0 && <span className="pill go">{b.delivered} delivered</span>}
            {b.sent > 0 && <span className="pill">{b.sent} sent</span>}
            {b.queued > 0 && <span className="pill wait">{b.queued} sending</span>}
            {b.failed > 0 && <span className="pill alarm">{b.failed} failed</span>}
          </div>
        </div>
      ))}
    </>
  )
}

/** The running record of the call, oldest first. A note added after the call
 *  closed is marked as a review note -- the server decides which it is.
 *  A line at a STAND-IN (083: the call-in or close of a call logged with
 *  that time not known) says so in its detail, written with it, and draws
 *  no time -- whatever is corrected later. */
export function ThreadRows({ thread }: { thread: ThreadEvent[] }) {
  const standIn = (e: ThreadEvent) => e.detail?.stand_in === true
  return (
    <>
      {thread.map((e, i) => (
        <div className={`ev ${e.kind === 'review_note' ? 'review' : ''} ${e.kind === 'corrected' ? 'corrected' : ''}`} key={i}>
          <div className="when mono">{standIn(e) ? '—' : hhmm(e.occurred_at)}</div>
          <div className="what">
            <div className="s">
              {e.kind === 'review_note' && <span className="pill">review note</span>}
              {/* A CORRECTION AND A CALL LOGGED AFTERWARDS (078) say so on
                  their line: neither is a step that happened when it says. */}
              {e.kind === 'corrected' && <span className="pill ccor-ed">correction</span>}
              {e.kind === 'logged_afterwards' && <span className="pill lgc-own">logged afterwards</span>}{' '}
              {e.summary}
            </div>
            {e.actor_label && <div className="who mono">{e.actor_label}</div>}
          </div>
          {e.source_system !== 'crm' && <span className="pill">{e.source_system}</span>}
        </div>
      ))}
    </>
  )
}
